import { useState } from "react";
import { Button } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import {
  useGetReservations,
  useGetReservation,
} from "../../utils/react-query/queryReservations/queries";
import {
  useCreateReservation,
  useEditReservation,
  useApproveReservation,
  useRejectReservation,
} from "../../utils/react-query/queryReservations/queries";
import {
  useMyPermissions,
  useMyUserId,
} from "../../utils/react-query/queryRbac/queries";
import { successToast, errorToast } from "../../services/toasts";
import { ReservationList } from "./ReservationList";
import { ReservationModal } from "./ReservationModal";
import { ReservationDetail } from "./ReservationDetail";
import { ReservationHistoryModal } from "./components/ReservationHistoryModal/ReservationHistoryModal";
import type { CreateReservationPayload, EditReservationPayload } from "./types";
import styles from "./ReservationsPage.module.scss";

export function ReservationsPage() {
  // Permissions
  const {
    data: permissions = new Set<string>(),
    isLoading: permissionsLoading,
  } = useMyPermissions();

  // Current user's numeric ID (from RBAC member record)
  const { data: currentUserId } = useMyUserId();

  const canViewAll = permissions.has("reservations:view_all");
  const canViewOwn = permissions.has("reservations:view_own");
  const canCreate = permissions.has("reservations:create");
  const canApprove = permissions.has("reservations:approve");
  const canEditOwn = permissions.has("reservations:edit_own");

  // Data
  const { data: reservationsData, isLoading } = useGetReservations();

  // Mutations
  const { mutateAsync: createReservation } = useCreateReservation();
  const { mutateAsync: editReservation } = useEditReservation();
  const { mutateAsync: approveReservation } = useApproveReservation();
  const { mutateAsync: rejectReservation } = useRejectReservation();

  // Modal state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingReservationId, setEditingReservationId] = useState<
    number | null
  >(null);
  const [viewingReservationId, setViewingReservationId] = useState<
    number | null
  >(null);
  const [historyReservationId, setHistoryReservationId] = useState<
    number | null
  >(null);

  // Fetch reservation detail when viewing
  const { data: viewingReservation, isLoading: isLoadingDetail } =
    useGetReservation(viewingReservationId ?? 0);

  // Fetch reservation detail when editing
  const { data: editingReservationData } = useGetReservation(
    editingReservationId ?? 0
  );

  // Permission gate
  if (!permissionsLoading && !canViewAll && !canViewOwn) {
    return (
      <div className={styles.noPermissions}>
        <p>No tienes permisos para ver reservas.</p>
      </div>
    );
  }

  const handleCreate = async (payload: CreateReservationPayload) => {
    try {
      await createReservation(payload);
      setIsCreateModalOpen(false);
      successToast("Reserva creada exitosamente");
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Error al crear la reserva";
      errorToast(message);
      // Re-throw so the modal can inspect for field-level errors
      throw err;
    }
  };

  const handleEdit = async (id: number, payload: EditReservationPayload) => {
    try {
      await editReservation({ id, payload });
      setEditingReservationId(null);
      successToast("Reserva actualizada exitosamente");
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Error al editar la reserva";
      errorToast(message);
    }
  };

  const handleApprove = async (id: number, reason?: string) => {
    try {
      await approveReservation({ id, reason });
      setViewingReservationId(null);
      successToast("Reserva aprobada exitosamente");
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Error al aprobar la reserva";
      errorToast(message);
    }
  };

  const handleReject = async (id: number, reason?: string) => {
    try {
      await rejectReservation({ id, reason });
      setViewingReservationId(null);
      successToast("Reserva rechazada");
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Error al rechazar la reserva";
      errorToast(message);
    }
  };

  // canEditOwn is used to show/hide the edit button
  // The backend verifies if the user actually created the reservation

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h2 className={styles.heading}>Reservas</h2>
        {canCreate && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setIsCreateModalOpen(true)}
          >
            Nueva Reserva
          </Button>
        )}
      </div>

      <ReservationList
        data={reservationsData?.results ?? []}
        isLoading={isLoading}
        canApprove={canApprove}
        canEditOwn={canEditOwn}
        canViewAll={canViewAll}
        currentUserId={currentUserId}
        onView={(row) => setViewingReservationId(row.id)}
        onEdit={(row) => setEditingReservationId(row.id)}
        onApprove={(row, reason) => handleApprove(row.id, reason)}
        onReject={(row, reason) => handleReject(row.id, reason)}
        onHistory={(row) => setHistoryReservationId(row.id)}
      />

      {/* Create Modal */}
      {isCreateModalOpen && (
        <ReservationModal
          open={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onCreate={handleCreate}
          onEdit={handleEdit}
        />
      )}

      {/* Edit Modal */}
      {editingReservationId && (
        <ReservationModal
          open={!!editingReservationId}
          reservation={editingReservationData ?? null}
          onClose={() => setEditingReservationId(null)}
          onCreate={handleCreate}
          onEdit={handleEdit}
        />
      )}

      {/* Detail Dialog */}
      {viewingReservationId && (
        <ReservationDetail
          open={!!viewingReservationId}
          reservation={viewingReservation ?? null}
          isLoading={isLoadingDetail}
          canApprove={canApprove}
          onClose={() => setViewingReservationId(null)}
          onApprove={handleApprove}
          onReject={handleReject}
        />
      )}

      {/* History Dialog */}
      {historyReservationId && (
        <ReservationHistoryModal
          open={!!historyReservationId}
          reservationId={historyReservationId}
          onClose={() => setHistoryReservationId(null)}
        />
      )}
    </div>
  );
}

export default ReservationsPage;
